'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

interface HubSpotFormConfig {
  portalId: string
  formId: string
  target: string
  region?: string
  onFormReady?: () => void
  onFormSubmitted?: ($form: unknown, data?: { redirectUrl?: string; submissionValues?: Record<string, unknown> }) => void
  __INTERNAL__CONTEXT?: { disableRedirect?: boolean }
}

const REDIRECT_TIMEOUT_MS = 2000

const sha256Hex = async (value: unknown): Promise<string | undefined> => {
  if (typeof value !== 'string' || !value.trim()) return undefined
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value.trim().toLowerCase()))
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return undefined
  }
}

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (config: HubSpotFormConfig) => void
      }
    }
    dataLayer?: Record<string, unknown>[]
  }
}

interface UseHubSpotFormProps {
  portalId: string
  formId: string
  formName: string
  region?: string
}

export const useHubSpotForm = ({ portalId, formId, formName, region = 'eu1' }: UseHubSpotFormProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFormReady, setIsFormReady] = useState(false)
  const formTargetId = `hubspot-form-${portalId}-${formId}`

  const createHubSpotForm = useCallback(() => {
    if (!window.hbspt || !containerRef.current) {
      // Log detailed error for debugging, show generic message to user
      console.error('HubSpot forms library not available or container ref missing')
      setError('Form temporarily unavailable. Please try again later.')
      setIsLoading(false)
      return
    }

    try {
      window.hbspt.forms.create({
        portalId: portalId,
        formId: formId,
        target: `#${formTargetId}`,
        onFormReady: () => {
          setIsFormReady(true)
          setIsLoading(false)
        },
        // Undocumented: we follow redirectUrl ourselves once GTM's tags finish
        __INTERNAL__CONTEXT: { disableRedirect: true },
        onFormSubmitted: async (_$form, { redirectUrl, submissionValues } = {}) => {
          const submit = containerRef.current?.querySelector<HTMLInputElement>('input[type="submit"]')
          if (redirectUrl && submit) {
            submit.disabled = true
            submit.value = 'Submitting...'
          }
          const emailSha256 = await sha256Hex(submissionValues?.email)
          let redirected = false
          const redirect = () => {
            if (redirected || !redirectUrl) return
            redirected = true
            // Replaces HubSpot's bfcache guard, which the flag skips
            window.addEventListener('pageshow', (e) => e.persisted && window.location.reload())
            window.location.assign(redirectUrl)
          }
          ;(window.dataLayer = window.dataLayer || []).push({
            event: 'formSubmitSuccess',
            formName,
            formId,
            ...(emailSha256 && { emailSha256 }),
            eventCallback: redirect,
            eventTimeout: REDIRECT_TIMEOUT_MS,
          })
          // No GTM (ad blockers, local dev) means no eventCallback
          setTimeout(redirect, REDIRECT_TIMEOUT_MS)
        },
      })
    } catch (err) {
      // Log detailed error for debugging, show generic message to user
      console.error('Failed to create HubSpot form:', err)
      setError('Form temporarily unavailable. Please try again later.')
      setIsLoading(false)
    }
  }, [portalId, formId, formName, formTargetId])

  useEffect(() => {
    if (!portalId || !formId || !containerRef.current) {
      // Log detailed error for debugging, show generic message to user
      console.error('Missing required form configuration:', { portalId: !!portalId, formId: !!formId, containerRef: !!containerRef.current })
      setError('Form temporarily unavailable. Please try again later.')
      setIsLoading(false)
      return
    }

    // Set the target ID on the container
    containerRef.current.id = formTargetId

    // Capture the current ref value for cleanup
    const currentContainer = containerRef.current

    // HubSpot script URL based on region
    const regionScriptUrls: Record<string, string> = {
      eu1: 'https://js-eu1.hsforms.net/forms/v2.js',
      ap1: 'https://js-ap1.hsforms.net/forms/v2.js',
    }
    const scriptSrc = regionScriptUrls[region] ?? 'https://js.hsforms.net/forms/v2.js'

    // A stale run must not create a form: a second instance of the same GUID double-fires onFormSubmitted.
    let cancelled = false
    const clearContainer = () => {
      // Cleanup: remove the form container content using captured ref
      if (currentContainer) {
        currentContainer.innerHTML = ''
      }
    }

    const found = document.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`)
    if (found?.dataset.state === 'error') found.remove()
    const existingScript = found?.isConnected ? found : null
    if (existingScript && window.hbspt) {
      // Deferred a tick so a StrictMode or remount cleanup can cancel it
      const timer = setTimeout(() => {
        if (!cancelled) createHubSpotForm()
      })
      return () => {
        cancelled = true
        clearTimeout(timer)
        clearContainer()
      }
    }

    const onLoad = () => {
      if (!cancelled) createHubSpotForm()
    }
    const onError = () => {
      if (cancelled) return
      // Log detailed error for debugging, show generic message to user
      console.error('Failed to load HubSpot forms script from:', scriptSrc)
      setError('Form temporarily unavailable. Please try again later.')
      setIsLoading(false)
    }

    const script = existingScript ?? document.createElement('script')
    script.addEventListener('load', onLoad)
    script.addEventListener('error', onError)

    if (!existingScript) {
      script.src = scriptSrc
      script.async = true
      script.charset = 'utf-8'
      script.addEventListener('error', () => (script.dataset.state = 'error'))
      document.body.appendChild(script)
    }

    return () => {
      cancelled = true
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
      clearContainer()
    }
  }, [portalId, formId, region, formTargetId, createHubSpotForm])

  return {
    containerRef,
    isLoading,
    error,
    isFormReady,
  }
}